/** Pure view for the shared Figma-linked segmented toggle. */
import React, { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Segment props prepared by the container. */
export interface PreparedViewModeToggleItem {
  readonly value: string;
  readonly label: string;
  readonly icon: React.ElementType;
  readonly testID: string;
  readonly isActive: boolean;
  readonly accessibilityState: { readonly selected: boolean };
  readonly onPress: () => void;
  readonly onKeyDown: (event: {
    readonly key: string;
    preventDefault: () => void;
  }) => void;
}

/** Props for the pure segmented-toggle view. */
interface ViewModeToggleViewProps {
  readonly items: readonly PreparedViewModeToggleItem[];
  readonly accessibilityLabel: string;
}

/**
 * Renders the gray toggle track and its accessible segments.
 *
 * One geometry for every consumer (SE-8486, rows W17/S11 — Figma
 * 529:24392): a 36 px track, 32 px segments that hug icon + label. The old
 * `compact` / `toolbar` presets drew the squad's at 32 tall and the
 * watchlist's with 80 px fixed segments; the frame draws neither.
 * @param root0 - Prepared view props.
 * @param root0.items - Segments with stable callbacks.
 * @param root0.accessibilityLabel - Accessible group name.
 */
const ViewModeToggleView = ({
  items,
  accessibilityLabel,
}: ViewModeToggleViewProps) => (
  <Stack
    direction="horizontal"
    testID="view-mode-toggle"
    {...({ role: "tablist", "aria-label": accessibilityLabel } as object)}
    className="h-9 items-center gap-1 rounded-sm bg-surface-muted p-micro"
  >
    {items.map(item => {
      const tone = item.isActive
        ? "text-content-primary"
        : "text-content-subtle";
      const labelClassName = item.isActive
        ? `text-title-sm font-heading ${tone}`
        : `text-title-sm ${tone}`;
      return (
        <Pressable
          key={item.value}
          onPress={item.onPress}
          testID={item.testID}
          accessibilityRole="tab"
          accessibilityState={item.accessibilityState}
          accessibilityLabel={`${item.label} view`}
          focusable={item.isActive}
          {...({
            tabIndex: item.isActive ? 0 : -1,
            onKeyDown: item.onKeyDown,
          } as object)}
          className={`h-8 flex-row items-center justify-center gap-1 rounded-sm px-2 focus-visible:ring-2 focus-visible:ring-outline-default ${
            item.isActive ? "bg-surface-base" : ""
          }`}
        >
          <Icon as={item.icon} size="sm" className={tone} />
          <Text variant="unstyled" className={labelClassName}>
            {item.label}
          </Text>
        </Pressable>
      );
    })}
  </Stack>
);

ViewModeToggleView.displayName = "ViewModeToggleView";
export default memo(ViewModeToggleView);
